import { useEffect, useState } from "react";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, ImageOff, ChevronLeft, ChevronRight } from "lucide-react";

const STATUSES = ["new","needs_review","saved","watchlisted","rejected","duplicate","archived"];
const PAGE_SIZE = 9;

function ResultImage({ src, domain }) {
  const [errored, setErrored] = useState(false);
  // Reset error state when src changes
  useEffect(() => { setErrored(false); }, [src]);
  if (!src || errored) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-ivory/30 gap-1.5">
        <ImageOff size={28} strokeWidth={1.2} />
        <span className="text-[0.6rem] uppercase tracking-[0.2em] text-ivory/40">{domain || "no preview"}</span>
      </div>
    );
  }
  return <img src={src} alt="" className="w-full h-full object-cover" onError={() => setErrored(true)} />;
}

export default function NewFinds() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("new");
  const [form, setForm] = useState({});

  const load = () => api
    .get(`/search-results?status=${status}&limit=${PAGE_SIZE}&offset=${(page-1)*PAGE_SIZE}`)
    .then(r => { setItems(r.data?.rows || []); setTotal(r.data?.total || 0); })
    .catch(() => {});
  useEffect(() => { load(); }, [status, page]);
  useEffect(() => { setPage(1); }, [status]);

  // Auto-enrich images for any visible rows that don't have one yet (silent).
  const [autoTried, setAutoTried] = useState(new Set());
  useEffect(() => {
    const missing = items.filter(r => !r.image_url).map(r => r.id);
    const fresh = missing.filter(id => !autoTried.has(id));
    if (fresh.length === 0) return;
    setAutoTried(prev => new Set([...prev, ...fresh]));
      api.post("/search-results/enrich-images", { ids: fresh })
        .then(({ data }) => {
          if (data?.enriched > 0) load();
        })
        .catch(() => {});
  }, [items]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title || !form.source_url) { toast.error("Title and source URL required"); return; }
    try {
      await api.post("/search-results", form);
      toast.success("Result added");
      setForm({}); load();
    } catch (er) { toast.error("Failed"); }
  };

  const update = async (id, newStatus) => {
    await api.put(`/search-results/${id}`, { status: newStatus });
    toast.success(`Marked ${newStatus}`); load();
  };

  const convertToProperty = async (id) => {
    try {
      toast.info("Scraping listing…");
      const { data } = await api.post(`/search-results/${id}/convert`);
      if (data.already_converted) {
        toast.info("Already converted — opening property");
      } else {
        toast.success("Property created from result");
      }
      window.location.href = `/admin/properties/${data.property.id}`;
    } catch (e) {
      toast.error("Convert failed");
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Discovery</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">New Finds</h1>
      <p className="text-ivory/50 mt-2 text-sm">Live results from SerpAPI runs · image, title, snippet and link captured.</p>

      <form onSubmit={add} className="luxury-card p-5 mt-8 grid grid-cols-1 md:grid-cols-5 gap-3">
        <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Title" value={form.title || ""} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="result-title" />
        <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm md:col-span-2" placeholder="Source URL" value={form.source_url || ""} onChange={e => setForm({ ...form, source_url: e.target.value })} data-testid="result-url" />
        <input className="bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm" placeholder="Location" value={form.possible_location || ""} onChange={e => setForm({ ...form, possible_location: e.target.value })} />
        <button className="btn-ruby justify-center" data-testid="add-result">Add Find</button>
      </form>

      <div className="mt-6 flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          {STATUSES.map(s => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`status-tab-${s}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] border ${status===s ? "border-gold text-gold" : "border-charcoal-600 text-ivory/60"}`}>
              {s.replace(/_/g," ")}
            </button>
          ))}
        </div>
        <button onClick={async () => {
            toast.info("Fetching images for existing results…");
            try {
              const { data } = await api.post("/search-results/enrich-images");
              toast.success(`Updated ${data.enriched} / ${data.checked}`);
              load();
            } catch (e) { toast.error("Image backfill failed"); }
          }}
          className="btn-outline !py-1 !px-3 !text-[0.65rem]"
          data-testid="enrich-images">
          Fetch missing images
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="new-finds-grid">
        {items.length === 0 && (
          <div className="luxury-card p-8 text-center text-ivory/40 text-sm col-span-full">No results.</div>
        )}
        {items.map(r => (
          <article key={r.id} className="luxury-card overflow-hidden flex flex-col" data-testid={`result-${r.id}`}>
            <div className="relative w-full aspect-[16/10] bg-gradient-to-br from-charcoal-700 to-charcoal-800 overflow-hidden">
              <ResultImage src={r.image_url} domain={r.source_domain} />
              {r.position && (
                <span className="absolute top-2 left-2 font-mono text-[0.6rem] uppercase tracking-[0.2em] text-gold bg-ink-950/70 px-2 py-0.5">
                  #{r.position}
                </span>
              )}
              {(r.opportunity_score || 0) > 0 && (
                <span
                  className={`absolute top-2 right-2 font-mono text-[0.65rem] font-medium px-2 py-0.5 border bg-ink-950/85 ${
                    r.opportunity_score >= 75 ? "text-emerald-300 border-emerald-500/50"
                      : r.opportunity_score >= 60 ? "text-gold border-gold/50"
                      : r.opportunity_score >= 40 ? "text-bronze border-bronze/50"
                      : "text-ruby-light border-ruby/50"
                  }`}
                  title={`Risk score: ${r.risk_score || 0}`}
                  data-testid={`serp-opp-${r.id}`}
                >
                  {r.opportunity_score}
                </span>
              )}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              <p className="text-[0.65rem] uppercase tracking-[0.18em] text-gold/80">{r.source_domain || "—"}</p>
              <h3 className="mt-2 text-ivory font-serif text-base leading-snug line-clamp-2">{r.title}</h3>
              {r.snippet && <p className="mt-2 text-[0.75rem] text-ivory/60 line-clamp-3">{r.snippet}</p>}
              {r.matched_keywords?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {r.matched_keywords.slice(0,4).map(k => (
                    <span key={k} className="text-[0.6rem] uppercase tracking-wider border border-gold/40 text-gold/80 px-1.5 py-0.5">{k}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 flex items-center justify-between gap-2">
                <a href={r.source_url} target="_blank" rel="noopener noreferrer"
                   className="text-[0.7rem] text-gold/80 hover:text-gold inline-flex items-center gap-1 truncate"
                   data-testid={`result-link-${r.id}`}>
                  <ExternalLink size={11} /> Open listing
                </a>
                {r.property_id && (
                  <span className="text-[0.6rem] uppercase tracking-[0.18em] text-gold border border-gold/40 px-1.5 py-0.5">linked</span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-1.5">
                <button onClick={() => convertToProperty(r.id)}
                  className="btn-ruby !py-1 !px-2 !text-[0.6rem] justify-center"
                  data-testid={`result-convert-${r.id}`}>
                  Save as Property
                </button>
                <button onClick={() => update(r.id, "watchlisted")} className="btn-outline !py-1 !px-2 !text-[0.6rem] justify-center">Watch</button>
                <button onClick={() => update(r.id, "saved")}       className="btn-outline !py-1 !px-2 !text-[0.6rem] justify-center">Save</button>
                <button onClick={() => update(r.id, "rejected")}    className="btn-outline !py-1 !px-2 !text-[0.6rem] justify-center">Reject</button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {/* Pagination */}
      <div className="mt-8 flex items-center justify-between text-xs text-ivory/50" data-testid="new-finds-pagination">
        <span>{total > 0 ? `${(page-1)*PAGE_SIZE + 1}–${Math.min(page*PAGE_SIZE, total)} of ${total}` : "—"}</span>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p-1)}
            className="btn-outline !py-1 !px-2 !text-[0.65rem] disabled:opacity-30">
            <ChevronLeft size={12} /> Prev
          </button>
          <span className="font-mono text-ivory/70">{page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage(p => p+1)}
            className="btn-outline !py-1 !px-2 !text-[0.65rem] disabled:opacity-30">
            Next <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </Container>
  );
}
